import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Card, Col, Container, Form, Image, InputGroup, Row, Spinner } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ConfirmationResult, RecaptchaVerifier, signInWithPhoneNumber, signInWithPopup, signOut } from "firebase/auth";
import { auth, googleProvider } from "../../config/firebase";
import {
  useConfirmStoreResetPasswordMutation,
  useConfirmStoreResetPasswordWithFirebaseMobileMutation,
  useFirebaseStoreAuthMutation,
  useRequestStoreForgotPasswordMutation,
  useStoreLoginMutation,
} from "../../redux/services/store/storeAuthApi";

type LoginMode = "otp" | "password" | "forgot";
type RedirectState = {
  forgotPassword?: boolean;
  from?: { pathname?: string; search?: string; hash?: string };
};

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

const RECAPTCHA_CONTAINER_ID = "firebase-recaptcha-container-global";
const STORE_LOGO =
  "https://cdn.mansercommerce.com/media/store_branding/1/header_logo_url/a87b19c1da9044f3a71202e129d0e69c.avif";

const isEmailOrMobile = (value: string) => {
  const input = String(value || "").trim();
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input) || /^\+[1-9]\d{7,14}$/.test(input);
};

const extractErrorMessage = (error: any, fallback: string) => {
  const detail = error?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    return detail.map((item: any) => item?.msg || "").filter(Boolean).join(", ") || fallback;
  }
  return error?.message || fallback;
};

const Signin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectState = location.state as RedirectState | null;
  const redirectPath = redirectState?.from?.pathname
    ? `${redirectState.from.pathname || ""}${redirectState.from.search || ""}${redirectState.from.hash || ""}`
    : "/";

  const [mode, setMode] = useState<LoginMode>(redirectState?.forgotPassword ? "forgot" : "otp");
  const [otpSent, setOtpSent] = useState(false);
  const [resetOtpSent, setResetOtpSent] = useState(false);
  const [passwordInputType, setPasswordInputType] = useState<"password" | "text">("password");
  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isForgotLoading, setIsForgotLoading] = useState(false);
  const [resetDevOtp, setResetDevOtp] = useState("");
  const [isHeroImageHovered, setIsHeroImageHovered] = useState(false);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const forgotConfirmationResultRef = useRef<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<RecaptchaVerifier | null>(null);

  const [firebaseStoreAuth, { isLoading: isGoogleLoading }] = useFirebaseStoreAuthMutation();
  const [storeLogin, { isLoading: isPasswordLoading }] = useStoreLoginMutation();
  const [requestStoreForgotPassword] = useRequestStoreForgotPasswordMutation();
  const [confirmStoreResetPassword] = useConfirmStoreResetPasswordMutation();
  const [confirmStoreResetPasswordWithFirebaseMobile] =
    useConfirmStoreResetPasswordWithFirebaseMobileMutation();

  const clearMessages = () => {
    setAuthError("");
    setAuthSuccess("");
  };

  const isMobileIdentifier = (value: string) => /^\+[1-9]\d{7,14}$/.test(String(value || "").trim());

  const resetRecaptcha = () => {
    try {
      recaptchaRef.current?.clear();
    } catch {}
    recaptchaRef.current = null;
    window.recaptchaVerifier = undefined;
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
    if (container) container.innerHTML = "";
  };

  const getOrCreateRecaptcha = async () => {
    if (recaptchaRef.current) return recaptchaRef.current;
    if (window.recaptchaVerifier) return window.recaptchaVerifier;
    const verifier = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, { size: "invisible" });
    await verifier.render();
    recaptchaRef.current = verifier;
    window.recaptchaVerifier = verifier;
    return verifier;
  };

  const loginToBackendWithFirebaseUser = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Firebase user not found");
    const idToken = await currentUser.getIdToken(true);
    await firebaseStoreAuth({
      id_token: idToken,
      device_type: "web",
      device_name: window.navigator.userAgent,
    }).unwrap();
    navigate(redirectPath, { replace: true });
  };

  const otpFormik = useFormik({
    initialValues: { mobile: "", otp: "" },
    validationSchema: useMemo(
      () =>
        Yup.object({
          mobile: Yup.string().required("Mobile number is required").matches(/^\+[1-9]\d{7,14}$/, "Enter mobile in format +919876543210"),
          otp: otpSent ? Yup.string().required("OTP is required") : Yup.string(),
        }),
      [otpSent]
    ),
    onSubmit: async (values) => {
      try {
        clearMessages();
        setIsOtpLoading(true);
        if (!otpSent) {
          resetRecaptcha();
          const verifier = await getOrCreateRecaptcha();
          confirmationResultRef.current = await signInWithPhoneNumber(auth, values.mobile, verifier);
          setOtpSent(true);
          return;
        }
        if (!confirmationResultRef.current) throw new Error("OTP session not found. Please resend OTP.");
        await confirmationResultRef.current.confirm(values.otp);
        await loginToBackendWithFirebaseUser();
      } catch (error: any) {
        setAuthError(extractErrorMessage(error, "OTP flow failed"));
      } finally {
        setIsOtpLoading(false);
      }
    },
  });

  const passwordFormik = useFormik({
    initialValues: { identifier: "", password: "" },
    validationSchema: Yup.object({
      identifier: Yup.string().required("Email or mobile is required").test("email-or-mobile", "Enter a valid email or mobile in format +919876543210", (value) => isEmailOrMobile(String(value || ""))),
      password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
    }),
    onSubmit: async (values) => {
      try {
        clearMessages();
        const identifier = values.identifier.trim();
        const isMobile = /^\+[1-9]\d{7,14}$/.test(identifier);
        await storeLogin({
          email: isMobile ? undefined : identifier,
          mobile_e164: isMobile ? identifier : undefined,
          password: values.password,
        }).unwrap();
        navigate(redirectPath, { replace: true });
      } catch (error: any) {
        setAuthError(extractErrorMessage(error, "Password login failed"));
      }
    },
  });

  const forgotFormik = useFormik({
    initialValues: { identifier: "", otp_code: "", new_password: "", confirm_password: "" },
    validationSchema: useMemo(
      () =>
        Yup.object({
          identifier: Yup.string().required("Email or mobile is required").test("email-or-mobile", "Enter a valid email or mobile in format +919876543210", (value) => isEmailOrMobile(String(value || ""))),
          otp_code: resetOtpSent ? Yup.string().required("OTP is required") : Yup.string(),
          new_password: resetOtpSent ? Yup.string().min(8, "Password must be at least 8 characters").required("New password is required") : Yup.string(),
          confirm_password: resetOtpSent ? Yup.string().oneOf([Yup.ref("new_password")], "Passwords do not match").required("Confirm password is required") : Yup.string(),
        }),
      [resetOtpSent]
    ),
    onSubmit: async (values) => {
      try {
        clearMessages();
        setIsForgotLoading(true);
        const identifier = values.identifier.trim();
        if (!resetOtpSent) {
          if (isMobileIdentifier(identifier)) {
            resetRecaptcha();
            const verifier = await getOrCreateRecaptcha();
            forgotConfirmationResultRef.current = await signInWithPhoneNumber(auth, identifier, verifier);
            setResetOtpSent(true);
            setResetDevOtp("");
            setAuthSuccess("Reset OTP sent successfully.");
            return;
          }
          const result = await requestStoreForgotPassword({ identifier: values.identifier.trim() }).unwrap();
          setResetOtpSent(true);
          setResetDevOtp(String(result?.otp_code || ""));
          setAuthSuccess(result?.otp_code ? `Reset OTP sent. Dev OTP: ${result.otp_code}` : "Reset OTP sent successfully.");
          return;
        }
        if (isMobileIdentifier(identifier)) {
          if (!forgotConfirmationResultRef.current) throw new Error("OTP session not found. Please resend OTP.");
          const credential = await forgotConfirmationResultRef.current.confirm(values.otp_code.trim());
          const idToken = await credential.user.getIdToken(true);
          await confirmStoreResetPasswordWithFirebaseMobile({
            id_token: idToken,
            mobile_e164: identifier,
            new_password: values.new_password,
            confirm_password: values.confirm_password,
          }).unwrap();
          await signOut(auth);
          forgotConfirmationResultRef.current = null;
        } else {
          await confirmStoreResetPassword({
            identifier,
            otp_code: values.otp_code.trim(),
            new_password: values.new_password,
            confirm_password: values.confirm_password,
          }).unwrap();
        }
        setAuthSuccess("Password reset successfully. Please login with your new password.");
        passwordFormik.setFieldValue("identifier", identifier, false);
        setMode("password");
        setResetOtpSent(false);
        setResetDevOtp("");
        forgotFormik.resetForm();
      } catch (error: any) {
        setAuthError(extractErrorMessage(error, "Password reset failed"));
      } finally {
        setIsForgotLoading(false);
      }
    },
  });

  useEffect(() => () => resetRecaptcha(), []);

  return (
    <section
      className="min-vh-100 position-relative d-flex align-items-center"
      style={{ background: "linear-gradient(135deg, #f7f1eb 0%, #fdfaf7 45%, #efe6dd 100%)" }}
    >
      <Container fluid className="px-0">
        <Row className="g-0 min-vh-100">
          <Col lg={6} className="d-none d-lg-block">
            <div
              className="h-100 position-relative overflow-hidden"
              onMouseEnter={() => setIsHeroImageHovered(true)}
              onMouseLeave={() => setIsHeroImageHovered(false)}
            >
              <div
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{
                  backgroundImage:
                    "url('https://cdn.mansercommerce.com/media/store_section_item/20/image_url/fabc36568f1b47688f281bf7918f2a61.avif')",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  filter: isHeroImageHovered ? "grayscale(0%)" : "grayscale(100%)",
                  transform: "scale(1.02)",
                  transition: "filter 280ms ease",
                }}
              />
              <div
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{
                  background: "linear-gradient(160deg, rgba(0,0,0,0.58), rgba(0,0,0,0.22))",
                }}
              />

              <div className="h-100 position-relative d-flex flex-column justify-content-end p-5">
                <div style={{ maxWidth: 430 }}>
                  <p className="text-uppercase text-white mb-3" style={{ letterSpacing: "0.30em", fontSize: 12 }}>
                    FionaZiya Bridal Couture
                  </p>
                  <h1 className="text-white mb-3" style={{ fontSize: "3rem", lineHeight: 1.1, fontWeight: 700 }}>
                    Enter the world of
                    <br />
                    timeless bridal elegance.
                  </h1>
                  <p className="text-white-50 mb-0 fs-5">
                    Sign in to explore FionaZiya collections, track your orders, save favorites, and
                    enjoy a seamless shopping journey.
                  </p>
                </div>

                <div className="text-white-50 small">
                  Crafted for graceful moments, modern celebrations, and unforgettable style.
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="h-100 d-flex align-items-center justify-content-center px-3 px-md-5 py-5">
              <div style={{ width: "100%", maxWidth: 470 }}>
                <div className="d-lg-none text-center mb-4">
                  <Link to="/" className="text-decoration-none">
                    <Image src={STORE_LOGO} alt="FionaZiya" height={30} />
                  </Link>
                </div>

                <Card className="border-0 rounded-5 shadow-sm p-4 p-md-5" style={{ border: "1px solid rgba(0,0,0,0.06)" }}>
                  <div className="mb-4 text-center text-lg-start">
                    <p className="text-uppercase text-muted mb-2" style={{ letterSpacing: "0.22em", fontSize: 12 }}>
                      Welcome Back
                    </p>
                    <h3 className="fw-bold mb-2" style={{ color: "#221a16" }}>
                      {mode === "forgot" ? "Reset your password" : "Sign in to FionaZiya"}
                    </h3>
                    <p className="text-muted mb-0">
                      {mode === "forgot"
                        ? "Use email or mobile, verify OTP, and set a new password."
                        : "Continue with Google, mobile OTP, or password."}
                    </p>
                  </div>

              {authError ? <Alert variant="danger">{authError}</Alert> : null}
              {authSuccess ? <Alert variant="success">{authSuccess}</Alert> : null}

              {mode !== "forgot" ? (
                <>
                  <div className="d-grid mb-4">
                    <Button variant="light" className="rounded-pill py-3 fw-semibold border" type="button" onClick={async () => {
                      try {
                        clearMessages();
                        const result = await signInWithPopup(auth, googleProvider);
                        const idToken = await result.user.getIdToken(true);
                        await firebaseStoreAuth({ id_token: idToken, device_type: "web", device_name: window.navigator.userAgent }).unwrap();
                        navigate(redirectPath, { replace: true });
                      } catch (error: any) {
                        setAuthError(extractErrorMessage(error, "Google login failed"));
                      }
                    }} disabled={isGoogleLoading || isOtpLoading}>
                      {isGoogleLoading ? <><Spinner size="sm" className="me-2" />Signing in...</> : <><i className="ri-google-fill me-2" />Continue with Google</>}
                    </Button>
                  </div>

                  <div className="d-flex justify-content-center mb-4">
                    <div className="d-inline-flex p-1 rounded-pill" style={{ background: "#f5eee8" }}>
                      <button type="button" onClick={() => setMode("otp")} className="border-0 px-4 py-2 rounded-pill fw-semibold" style={{ background: mode === "otp" ? "#201614" : "transparent", color: mode === "otp" ? "#fff" : "#5c4a43" }}>OTP</button>
                      <button type="button" onClick={() => setMode("password")} className="border-0 px-4 py-2 rounded-pill fw-semibold" style={{ background: mode === "password" ? "#201614" : "transparent", color: mode === "password" ? "#fff" : "#5c4a43" }}>Password</button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="mb-4">
                  <Button variant="link" className="p-0 text-decoration-none" style={{ color: "#7a5d50" }} onClick={() => setMode("password")}>
                    <i className="ri-arrow-left-line me-1" />Back to password login
                  </Button>
                </div>
              )}

              {mode === "otp" ? (
                <Form onSubmit={otpFormik.handleSubmit}>
                  <div className="mb-3">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control name="mobile" value={otpFormik.values.mobile} onChange={otpFormik.handleChange} onBlur={otpFormik.handleBlur} disabled={otpSent || isOtpLoading} placeholder="+919876543210" />
                    {otpFormik.touched.mobile && otpFormik.errors.mobile ? <div className="text-danger mt-1 small">{otpFormik.errors.mobile}</div> : null}
                  </div>
                  {otpSent ? (
                    <div className="mb-3">
                      <Form.Label>Enter OTP</Form.Label>
                      <Form.Control name="otp" value={otpFormik.values.otp} onChange={otpFormik.handleChange} onBlur={otpFormik.handleBlur} placeholder="Enter OTP" />
                    </div>
                  ) : null}
                  <div id={RECAPTCHA_CONTAINER_ID} style={{ minHeight: 1 }} />
                  <Button type="submit" className="w-100 rounded-pill py-3 fw-semibold border-0" disabled={isOtpLoading} style={{ background: "#201614" }}>
                    {isOtpLoading ? <><Spinner size="sm" className="me-2" />{otpSent ? "Verifying..." : "Sending OTP..."}</> : otpSent ? "Verify & Login" : "Send OTP"}
                  </Button>
                </Form>
              ) : null}

              {mode === "password" ? (
                <Form onSubmit={passwordFormik.handleSubmit}>
                  <div className="mb-3">
                    <Form.Label>Email or Mobile</Form.Label>
                    <Form.Control name="identifier" value={passwordFormik.values.identifier} onChange={passwordFormik.handleChange} onBlur={passwordFormik.handleBlur} placeholder="Enter email or +919876543210" />
                    {passwordFormik.touched.identifier && passwordFormik.errors.identifier ? <div className="text-danger mt-1 small">{passwordFormik.errors.identifier}</div> : null}
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <Form.Label className="mb-0">Password</Form.Label>
                      <button type="button" className="btn btn-link small text-decoration-none p-0" style={{ color: "#7a5d50" }} onClick={() => setMode("forgot")}>Forgot password?</button>
                    </div>
                    <InputGroup>
                      <Form.Control type={passwordInputType} name="password" value={passwordFormik.values.password} onChange={passwordFormik.handleChange} onBlur={passwordFormik.handleBlur} placeholder="Enter password" />
                      <Button type="button" variant="light" onClick={() => setPasswordInputType((prev) => (prev === "password" ? "text" : "password"))}>
                        <i className={passwordInputType === "password" ? "ri-eye-line" : "ri-eye-off-line"} />
                      </Button>
                    </InputGroup>
                    {passwordFormik.touched.password && passwordFormik.errors.password ? <div className="text-danger mt-1 small">{passwordFormik.errors.password}</div> : null}
                  </div>
                  <Button type="submit" className="w-100 rounded-pill py-3 fw-semibold border-0" disabled={isPasswordLoading} style={{ background: "#201614" }}>
                    {isPasswordLoading ? <><Spinner size="sm" className="me-2" />Signing in...</> : "Login with Password"}
                  </Button>
                </Form>
              ) : null}

              {mode === "forgot" ? (
                <Form onSubmit={forgotFormik.handleSubmit}>
                  <div className="mb-3">
                    <Form.Label>Email or Mobile</Form.Label>
                    <Form.Control name="identifier" value={forgotFormik.values.identifier} onChange={forgotFormik.handleChange} onBlur={forgotFormik.handleBlur} disabled={resetOtpSent || isForgotLoading} placeholder="Enter email or +919876543210" />
                    {forgotFormik.touched.identifier && forgotFormik.errors.identifier ? <div className="text-danger mt-1 small">{forgotFormik.errors.identifier}</div> : null}
                  </div>
                  {resetOtpSent ? (
                    <>
                      <div className="mb-3">
                        <Form.Label>Enter OTP</Form.Label>
                        <Form.Control name="otp_code" value={forgotFormik.values.otp_code} onChange={forgotFormik.handleChange} onBlur={forgotFormik.handleBlur} placeholder="Enter OTP" />
                        {resetDevOtp ? <div className="text-muted mt-2 small">Dev OTP: <strong>{resetDevOtp}</strong></div> : null}
                      </div>
                      <div className="mb-3">
                        <Form.Label>New Password</Form.Label>
                        <Form.Control type={passwordInputType} name="new_password" value={forgotFormik.values.new_password} onChange={forgotFormik.handleChange} onBlur={forgotFormik.handleBlur} placeholder="Enter new password" />
                      </div>
                      <div className="mb-3">
                        <Form.Label>Confirm Password</Form.Label>
                        <Form.Control type={passwordInputType} name="confirm_password" value={forgotFormik.values.confirm_password} onChange={forgotFormik.handleChange} onBlur={forgotFormik.handleBlur} placeholder="Confirm password" />
                      </div>
                    </>
                  ) : null}
                  <Button type="submit" className="w-100 rounded-pill py-3 fw-semibold border-0" disabled={isForgotLoading} style={{ background: "#201614" }}>
                    {isForgotLoading ? <><Spinner size="sm" className="me-2" />{resetOtpSent ? "Resetting..." : "Sending OTP..."}</> : resetOtpSent ? "Reset Password" : "Send Reset OTP"}
                  </Button>
                </Form>
              ) : null}
                </Card>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Signin;
